import { useEffect, useState } from 'react'
import { message } from 'antd'
import { upcomingPayments, notifications, monthlyRevenue, alertItems } from '../data/mockData'
import { countInvoices, invoicesAggregate } from '../../../utils'
import store from '../../../../../store'
import { useDispatch } from 'react-redux'
import coreReducer from '../../../../../core/utils/reducer'
import CoreEnums from '../../../../../core/utils/enums'
import { InvoiceForm } from '../../Invoicing'

/**
 * Custom hook to handle dashboard data loading and state
 * In a real implementation, this would fetch data from APIs
 */
const useDashboardData = () => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({
    recentInvoices: [],
    upcomingPayments: [],
    notifications: [],
    monthlyRevenue: [],
    alertItems: [],
    collectionPerformance: [],
    financialSummary: {
      totalRevenue: 0,
      outstandingInvoices: 0,
      pendingClaims: 0,
      collectionRate: 0
    }
  })

  const handleEditInvoice = (record) => {
    dispatch(
      coreReducer.actions.addTab({
        key: `${CoreEnums.tabKeys.INVOICES}_${record._id}`,
        closable: true,
        label: `Invoice: ${record.fullName}`,
        children: <InvoiceForm data={record} isNew={false} />
      })
    )
  }

  const fetchData = async () => {
    try {
      setLoading(true)

      const promiseArray = [
        invoicesAggregate([
          {
            $match: {
              entityRef: store.getState().core.entity._id,
              createdAt: {
                $gte: new Date(new Date().setDate(new Date().getDate() - 1)),
                $lte: new Date()
              }
            }
          },
          {
            $addFields: {
              userRefObjectId: { $cond: [{ $eq: ['$userRef', null] }, null, { $toObjectId: '$userRef' }] },
              clinicRefObjectId: { $cond: [{ $eq: ['$clinicRef', null] }, null, { $toObjectId: '$clinicRef' }] },
              medicalProfRefObjectId: {
                $cond: [{ $eq: ['$medicalProfRef', null] }, null, { $toObjectId: '$medicalProfRef' }]
              },
              refMedProfRefObjectId: {
                $cond: [{ $eq: ['$refMedProfRef', null] }, null, { $toObjectId: '$refMedProfRef' }]
              },
              assMedProfRefObjectId: {
                $cond: [{ $eq: ['$assMedProfRef', null] }, null, { $toObjectId: '$assMedProfRef' }]
              }
            }
          },
          {
            $lookup: {
              from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_billing_records`,
              localField: 'bookingRef',
              foreignField: 'bookingRef',
              as: 'billingRecords'
            }
          },
          {
            $lookup: {
              from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
              localField: 'userRefObjectId',
              foreignField: '_id',
              as: 'patientRecord'
            }
          },
          {
            $lookup: {
              from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_admin_clinics`,
              localField: 'clinicRefObjectId',
              foreignField: '_id',
              as: 'clinicRecord'
            }
          },
          {
            $lookup: {
              from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
              localField: 'medicalProfRefObjectId',
              foreignField: '_id',
              as: 'medicalProfRecord'
            }
          },
          {
            $lookup: {
              from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
              localField: 'refMedProfRefObjectId',
              foreignField: '_id',
              as: 'refMedProfRecord'
            }
          },
          {
            $lookup: {
              from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
              localField: 'assMedProfRefObjectId',
              foreignField: '_id',
              as: 'assMedProfRecord'
            }
          },
          {
            $unwind: '$patientRecord'
          },
          {
            $unwind: '$clinicRecord'
          },
          {
            $project: {
              patientRecord: {
                profileImage: 0
              },
              clinicRecord: {
                clinicLogo: 0
              },
              medicalProfRecord: {
                profileImage: 0
              },
              refMedProfRecord: {
                profileImage: 0
              },
              assMedProfRecord: {
                profileImage: 0
              }
            }
          }
        ]),
        countInvoices({
          entityRef: store.getState().core.entity._id,
          status: ''
        }),
        invoicesAggregate([
          {
            $match: {
              entityRef: store.getState().core.entity._id,
              createdAt: {
                $gte: new Date(new Date().setDate(new Date().getDate() - 30)),
                $lte: new Date()
              }
            }
          },
          {
            $lookup: {
              from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_patient_payments`,
              let: { invoiceId: '$_id' },
              pipeline: [
                {
                  $match: {
                    $expr: {
                      $eq: ['$invoiceRef', { $toString: '$$invoiceId' }]
                    }
                  }
                }
              ],
              as: 'patientPayments'
            }
          },
          {
            $lookup: {
              from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_billing_records`,
              localField: 'bookingRef',
              foreignField: 'bookingRef',
              as: 'billingRecords'
            }
          },
          {
            $project: {
              patientPayments: {
                paymentAmount: 1
              },
              billingRecords: {
                total: 1
              },
              invoiceType: 1,
              status: 1
            }
          }
        ]),
        invoicesAggregate([
          {
            $match: {
              entityRef: store.getState().core.entity._id,
              createdAt: {
                $gte: new Date(new Date().setMonth(new Date().getMonth() - 4)),
                $lte: new Date()
              }
            }
          },
          {
            $lookup: {
              from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_patient_payments`,
              let: { invoiceId: '$_id' },
              pipeline: [
                {
                  $match: {
                    $expr: {
                      $eq: ['$invoiceRef', { $toString: '$$invoiceId' }]
                    }
                  }
                }
              ],
              as: 'patientPayments'
            }
          },
          {
            $project: {
              _id: 1,
              createdAt: 1,
              patientPayments: {
                paymentAmount: 1
              }
            }
          }
        ])
      ]

      const [recentInvoices, pendingClaims, invoicePayments, monthlyInvoices] = await Promise.all(promiseArray)

      // Process monthly revenue on the client side
      const monthlyRevenueMap = {}
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

      // Initialize with the last 5 months (including current)
      const today = new Date()
      for (let i = 4; i >= 0; i--) {
        const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1)
        const monthKey = monthNames[monthDate.getMonth()]
        monthlyRevenueMap[monthKey] = 0
      }

      // Calculate revenue by month
      monthlyInvoices.forEach((invoice) => {
        if (invoice.patientPayments && invoice.patientPayments.length > 0) {
          const invoiceDate = new Date(invoice.createdAt)
          const monthKey = monthNames[invoiceDate.getMonth()]

          // Sum all payments for this invoice
          const invoiceTotal = invoice.patientPayments.reduce(
            (acc, payment) => acc + parseFloat(payment.paymentAmount || 0),
            0
          )

          // Add to the appropriate month
          if (monthlyRevenueMap[monthKey] !== undefined) {
            monthlyRevenueMap[monthKey] += invoiceTotal
          }
        }
      })

      // Convert to array format matching the mockData
      const monthlyRevenueData = Object.keys(monthlyRevenueMap).map((month) => ({
        month,
        revenue: monthlyRevenueMap[month]
      }))

      // Filter out invoices with payments
      const invoicesWithPayments = invoicePayments.filter((invoice) => invoice.patientPayments.length > 0)

      const totalRev = invoicesWithPayments.reduce(
        (acc, curr) => acc + curr.patientPayments.reduce((acc, curr) => acc + parseFloat(curr.paymentAmount), 0),
        0
      )

      let totalOutstanding = 0

      invoicePayments.forEach((invoice) => {
        if (invoice.status !== 'completed') {
          const totalPayments = invoice.patientPayments.reduce((acc, curr) => acc + parseFloat(curr.paymentAmount), 0)
          const totalBilling = invoice.billingRecords.reduce((acc, curr) => acc + parseFloat(curr.total), 0)
          totalOutstanding += totalBilling - totalPayments
        }
      })

      const collectionRate = totalOutstanding > 0 ? (totalRev / totalOutstanding) * 100 : 100

      const collectionPerformance = []

      const invoiceTypes = [...new Set(invoicePayments.map((invoice) => invoice.invoiceType))]
      invoiceTypes.forEach((invoiceType) => {
        const invoices = invoicePayments.filter((invoice) => invoice.invoiceType === invoiceType)
        const percent =
          (invoices.filter((invoice) => invoice.patientPayments.length > 0).length / invoices.length) * 100
        collectionPerformance.push({
          title: invoiceType.toUpperCase(),
          percent: percent.toFixed(2)
        })
      })

      // In a real implementation, these would be API calls
      setData({
        recentInvoices,
        upcomingPayments,
        notifications,
        monthlyRevenue: monthlyRevenueData,
        alertItems,
        collectionPerformance,
        financialSummary: {
          totalRevenue: totalRev,
          outstandingInvoices: totalOutstanding,
          pendingClaims: pendingClaims.result,
          collectionRate
        }
      })
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      message.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  // Simulate API call to get dashboard data
  useEffect(() => {
    fetchData()
  }, [])

  const markAllNotificationsAsRead = () => {
    setData({
      ...data,
      notifications: data.notifications.map((notification) => ({
        ...notification,
        read: true
      }))
    })

    message.success('All notifications marked as read')
  }

  return {
    loading,
    data,
    refreshData: fetchData,
    markAllNotificationsAsRead,
    handleEditInvoice
  }
}

export default useDashboardData
